import {entries} from '@benchling/js-utils';
import {TextInput} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useMemo, useState} from 'react';

import {StructuredTableDict} from 'notebook/documents/DocumentConstants';
import {RegistrationTableInsertType} from 'notebook/integrations/registry/RegistrationTableConstants';
import {
  StructuredTableKind,
  StructuredTableMetadata,
} from 'notebook/structured-tables/StructuredTableConstants';
import * as Colors from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import DeleteIcon from 'ui-library/icons/DeleteIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {isEnterKey, isEscapeKey} from 'util/Keyboard';
import {ElementKeyboardEvent} from 'util/Types';

import {getEntryTables} from '../StructuredTableDAO';

export type AllStructuredTableMetadata = StructuredTableMetadata & {
  [key: string]: string | number | boolean | null | undefined;
};

interface StructuredTableWithApiId extends StructuredTableDict {
  apiId: string;
}

export enum TableSelectionType {
  EXISTING = 'existing',
  NEW = 'new',
}

export interface TableInfo {
  apiId: string;
  name: string;
}

export type TableSelection =
  | {type: TableSelectionType.EXISTING; table: TableInfo}
  | {type: TableSelectionType.NEW; name: string}
  | null;

export interface EntryTableDropdownProps {
  entryApiId?: string;
  onTableSelect: (selection: TableSelection) => void;
  selectedTableId?: string;
  tableKind?: StructuredTableKind;
  schemaId?: string;
  omitRowAndColumnData?: boolean;
  disabled?: boolean;
  placeholder?: string;
  onSelectCreateNewTable?: () => void;
  metadataFilters?: Partial<AllStructuredTableMetadata>;
}

/**
 * Dropdown to select a structured table from an entry
 *
 * @param entryApiId (string) - The API ID of the entry to fetch tables from
 * @param onTableSelect ((TableSelection) => void) - Callback used when a table is selected
 * @param selectedTableId (string) - The API ID of the currently selected table
 * @param tableKind (StructuredTableKind) - The kind of table to fetch
 * @param schemaId (string) - The schema ID to filter tables by
 * @param omitRowAndColumnData (boolean) - Whether to omit row and column data from the tables
 * @param disabled (boolean) - Whether the dropdown is disabled
 * @param placeholder (string) - The placeholder text to display when no table is selected
 * @param onSelectCreateNewTable (() => void) - If provided, a "Create new table" option will also be shown, and this callback will be used when it is selected
 * @param metadataFilters (Partial<AllStructuredTableMetadata>) - Filter tables by given metadata properties
 */
export default function EntryTableDropdown({
  entryApiId,
  onTableSelect,
  selectedTableId,
  tableKind,
  schemaId,
  omitRowAndColumnData = true,
  disabled = false,
  placeholder = 'Select table',
  onSelectCreateNewTable,
  metadataFilters,
}: EntryTableDropdownProps): JSX.Element {
  const [tables, setTables] = useState<Array<StructuredTableWithApiId>>([]);
  const [selectedTable, setSelectedTable] = useState<StructuredTableWithApiId | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!entryApiId) {
      setTables([]);
      return;
    }
    setIsLoading(true);
    async function fetchTables(): Promise<void> {
      entryApiId = entryApiId!;
      try {
        const response = await getEntryTables({
          entryApiId,
          tableKind,
          schemaId,
          omitRowAndColumnData,
        });

        const filteredTables: Array<StructuredTableWithApiId> = (
          response.tables as Array<StructuredTableWithApiId>
        ).filter((table) =>
          entries(metadataFilters ?? {}).every(([key, expectedValue]) => {
            const metadata = table.metadata as AllStructuredTableMetadata;
            return metadata?.[key] === expectedValue;
          })
        );
        setTables(filteredTables);
      } catch (error) {
        setTables([]);
        throw error;
      } finally {
        setIsLoading(false);
      }
    }

    fetchTables();
  }, [entryApiId, tableKind, schemaId, omitRowAndColumnData, metadataFilters]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (!selectedTableId) {
      setSelectedTable(null);
      return;
    }
    // Set selectedTable to the table with the matching apiId, or clear if it's not found
    if (tables.some((table) => table.apiId === selectedTableId)) {
      setSelectedTable(tables.find((table) => table.apiId === selectedTableId)!);
    } else {
      setSelectedTable(null);
    }
  }, [selectedTableId, tables, isLoading]);

  const handleTableSelect = (table: StructuredTableWithApiId): void => {
    setSelectedTable(table);

    onTableSelect({
      type: TableSelectionType.EXISTING,
      table: {
        apiId: table.apiId,
        name: table.name,
      },
    });
  };

  if (isLoading && !disabled) {
    return (
      <div className={classNames('entryTableDropdown', css(styles.container))}>
        <div className={css(styles.dropdown, styles.loadingBar)}>
          <CenteredHorizontalThrobber />
        </div>
      </div>
    );
  }

  const createTableOption = onSelectCreateNewTable ? (
    <Option
      childrenClassName={css(styles.createTableOption)}
      key="create_table"
      value="create_table"
      onSelect={() => {
        onSelectCreateNewTable();
      }}
    >
      {tableKind ? `Create new ${tableKind.toLowerCase()} table` : 'Create new table'}
    </Option>
  ) : null;

  return (
    <div className={classNames('entryTableDropdown', css(styles.container))}>
      <SelectDropdown
        className={css(styles.dropdown)}
        disabled={disabled || isLoading || (tables.length === 0 && !onSelectCreateNewTable)}
        displayName={selectedTable?.name || placeholder}
        menu={
          <Menu
            height={Menu.HEIGHTS.MEDIUM}
            width={Menu.WIDTHS.FULL}
            scrollable={true}
            value={selectedTable?.name}
          >
            {createTableOption}
            <Option
              key="none"
              value={null}
              onSelect={() => {
                onTableSelect(null);
              }}
            >
              None
            </Option>
            {tables.map((table) => (
              <Option
                key={table.apiId}
                value={table.name}
                onSelect={() => {
                  handleTableSelect(table);
                }}
              >
                {table.name}
              </Option>
            ))}
          </Menu>
        }
      />
    </div>
  );
}

export interface EntryTableDropdownWithCreationProps {
  entryApiId?: string;
  onTableSelect: (selection: TableSelection) => void;
  onSelectCreateNewTable: (tableName: string) => void;
  onClearTableName: () => void;
  selectedTable: TableSelection;
  tableKind?: StructuredTableKind;
  schemaId?: string;
  disabled?: boolean;
  placeholder?: string;
  onEnter?: (e: ElementKeyboardEvent) => void;
}

/**
 * Enhanced version of EntryTableDropdown that supports creating new tables with inline text input
 * when the "Create new table" option is selected
 */
export function EntryTableDropdownWithCreation({
  entryApiId,
  onTableSelect,
  onSelectCreateNewTable,
  onClearTableName,
  selectedTable,
  tableKind,
  schemaId,
  disabled = false,
  placeholder,
  onEnter,
}: EntryTableDropdownWithCreationProps): JSX.Element {
  const isCreatingTable = selectedTable?.type === TableSelectionType.NEW;
  const tableName = selectedTable?.type === TableSelectionType.NEW ? selectedTable.name : '';
  const selectedTableId =
    selectedTable?.type === TableSelectionType.EXISTING ? selectedTable.table.apiId : undefined;

  const memoizedMetadataFilters = useMemo(
    () =>
      tableKind === StructuredTableKind.REGISTRATION
        ? {insertType: RegistrationTableInsertType.REGISTER_EXISTING_ENTITIES}
        : undefined,
    [tableKind]
  );

  const handleCreateTableClick = (): void => {
    onSelectCreateNewTable('');
  };

  const handleClearTableName = (): void => {
    onClearTableName();
  };

  const handleTableNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    onSelectCreateNewTable(e.target.value);
  };

  const handleTableNameSubmit = (): void => {
    if (tableName.trim()) {
      onSelectCreateNewTable(tableName.trim());
    }
  };

  const handleTableNameKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (isEnterKey(e)) {
      handleTableNameSubmit();
      if (onEnter) {
        onEnter(e);
      }
    } else if (isEscapeKey(e)) {
      handleClearTableName();
    }
  };

  return (
    <>
      {/* Hide dropdown instead of ommitting to keep state and avoid requerying on text clear */}
      <div className={css(isCreatingTable && styles.hidden)}>
        <EntryTableDropdown
          entryApiId={entryApiId}
          onTableSelect={onTableSelect}
          selectedTableId={selectedTableId}
          tableKind={tableKind}
          schemaId={schemaId}
          disabled={disabled}
          placeholder={placeholder}
          onSelectCreateNewTable={handleCreateTableClick}
          metadataFilters={memoizedMetadataFilters}
        />
      </div>
      {isCreatingTable && (
        <div className={css(styles.inputContainer)}>
          <TextInput
            className={css(styles.textInput)}
            value={tableName}
            onChange={handleTableNameChange}
            onKeyDown={handleTableNameKeyPress}
            placeholder="Enter table name"
            autoFocus={true}
          />
          <DeleteIcon icon="cross-thick" onClick={handleClearTableName} />
        </div>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
  },
  createTableOption: {
    color: Colors.BLUE,
  },
  dropdown: {
    flex: 1,
    '& button': {
      height: '30px',
      minHeight: '30px',
      boxSizing: 'border-box',
    },
  },
  loadingBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '28px',
    border: `1px solid ${Colors.GRAY}`,
    borderRadius: '4px',
    backgroundColor: Colors.WHITE,
    boxSizing: 'border-box',
  },
  hidden: {
    display: 'none',
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  textInput: {
    flex: 1,
    '& .mantine-TextInput-input': {
      borderColor: Colors.GRAY,
      borderRadius: '4px',
      height: '30px',
      minHeight: '30px',
      borderWidth: '1px',
      padding: '4px 8px',
      boxSizing: 'border-box',
      '&:focus': {
        borderColor: Colors.BLUE_LIGHT,
      },
    },
  },
});
